import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const CABINET_KEY = 'activeCabinet';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function readStoredCabinet() {
  try {
    const raw = sessionStorage.getItem(CABINET_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeCabinet, setActiveCabinetState] = useState(() => readStoredCabinet());

  const setActiveCabinet = useCallback((cab) => {
    if (cab && cab.id) {
      sessionStorage.setItem(CABINET_KEY, JSON.stringify(cab));
      setActiveCabinetState(cab);
    } else {
      sessionStorage.removeItem(CABINET_KEY);
      setActiveCabinetState(null);
    }
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const sessionUser = sessionStorage.getItem('user');
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');

        if (sessionUser && token) {
          try {
            const response = await fetch('/api/auth/verify', {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                const userData = JSON.parse(sessionUser);
                setUser(userData);
                setIsAuthenticated(true);
                setActiveCabinetState(readStoredCabinet());
              } else {
                sessionStorage.removeItem('user');
                sessionStorage.removeItem('token');
                sessionStorage.removeItem(CABINET_KEY);
                localStorage.removeItem('token');
              }
            } else {
              sessionStorage.removeItem('user');
              sessionStorage.removeItem('token');
              sessionStorage.removeItem(CABINET_KEY);
              localStorage.removeItem('token');
            }
          } catch (error) {
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem(CABINET_KEY);
            localStorage.removeItem('token');
          }
        }
      } catch (error) {
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem(CABINET_KEY);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    sessionStorage.setItem('user', JSON.stringify(userData));
    if (userData.token) {
      sessionStorage.setItem('token', userData.token);
      localStorage.setItem('token', userData.token);
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setActiveCabinetState(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem(CABINET_KEY);
    localStorage.removeItem('token');
  };

  const isAdmin = () => {
    if (!user) return false;
    return (
      user.access_level === 'SuperAdmin' ||
      user.access_level === 'Admin' ||
      user.role === 'admin'
    );
  };

  const isCaissier = () => {
    if (!user) return false;
    return user.access_level === 'User' || user.role === 'caissier';
  };

  const isSuperAdmin = () => {
    return user && user.access_level === 'SuperAdmin';
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    isAdmin,
    isCaissier,
    isSuperAdmin,
    activeCabinet,
    setActiveCabinet,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
